import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Container, Button, Form, Row, Col, Table, Modal, Alert, Nav, Badge, OverlayTrigger, Tooltip } from 'react-bootstrap';
import './InvoiceDetail.css';
import api from '../services/api';
import SearchableSelect from '../components/SearchableSelect';
import { showToast } from '../components/ToastNotification';
import { exportMoneyXml, nazevDokladu } from '../components/invoiceActions';
import InvoiceEmailModal from '../components/InvoiceEmailModal';
import InvoiceAdvanceModal from '../components/InvoiceAdvanceModal';
import InvoiceDeleteModal from '../components/InvoiceDeleteModal';
import InvoiceCreditNoteModal from '../components/InvoiceCreditNoteModal';
import { confirm } from '../components/ConfirmDialog';

/** Formy úhrady, které znamenají barter — stejný seznam jako `InvoiceService::BARTER_PAYMENT_TYPES`. */
const BARTEROVE_UHRADY = ['Reciprocal', 'MediaBarter'];

// ── Helpers ─────────────────────────────────────────────────────────────
const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const datePart = dateStr.substring(0, 10);
  const d = new Date(datePart + 'T00:00:00');
  if (isNaN(d.getTime())) return dateStr;
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}.${month}.${year}`;
};

const formatPrice = (value, currency) => {
  if (value === null || value === undefined || value === '') return '—';
  const num = parseFloat(value);
  if (isNaN(num)) return '—';
  const formatted = num.toLocaleString('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return currency ? `${formatted} ${currency}` : formatted;
};

const toDateInput = (dateStr) => {
  if (!dateStr) return '';
  return dateStr.substring(0, 10);
};

const toDateTimeInput = (dateStr) => {
  if (!dateStr) return '';
  // Handle "Y-m-d H:i:s" → "Y-m-dTH:i"
  return dateStr.replace(' ', 'T').substring(0, 16);
};

// ── Badge components ───────────────────────────────────────────────────
function StatusBadge({ status }) {
  const map = {
    uhrazena:   { cls: 'badge-paid',      label: 'Uhrazená' },
    castecna:   { cls: 'badge-partial',    label: 'Částečně' },
    neuhrazena: { cls: 'badge-unpaid',     label: 'Neuhrazená' },
    storno:     { cls: 'badge-cancelled',  label: 'Storno' },
  };
  const m = map[status];
  if (!m) return <span>—</span>;
  return <span className={`badge-status ${m.cls}`}>{m.label}</span>;
}

function DirectionBadge({ direction }) {
  if (direction === 'Income') return <span className="badge-direction-income">Příjem</span>;
  if (direction === 'Expense') return <span className="badge-direction-expense">Výdaj</span>;
  return <span className="badge-direction-other">{direction || '—'}</span>;
}

// ── Related invoice autocomplete ───────────────────────────────────────
function RelatedInvoiceSearch({ value, displayName, onChange }) {
  const [search, setSearch] = useState(displayName || '');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    setSearch(displayName || '');
  }, [displayName]);

  useEffect(() => {
    const handleClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleSearch = (val) => {
    setSearch(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (val.length < 1) {
      setResults([]);
      setShowDropdown(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const result = await api.searchInvoices(val, 10);
        setResults(result.data || []);
        setShowDropdown(true);
      } catch (err) {
        console.error('Invoice search failed:', err);
      } finally {
        setLoading(false);
      }
    }, 300);
  };

  const handleSelect = (inv) => {
    setSearch(`${inv.number} — ${inv.customer_name}`);
    setShowDropdown(false);
    onChange(inv);
  };

  const handleClear = () => {
    setSearch('');
    setResults([]);
    setShowDropdown(false);
    onChange(null);
  };

  return (
    <Form.Group>
      <Form.Label>Navázaná faktura</Form.Label>
      <div className="invoice-autocomplete-wrapper" ref={wrapperRef}>
        <div className="position-relative">
          <Form.Control
            type="text"
            placeholder="Hledat dle čísla nebo firmy…"
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            onFocus={() => { if (results.length > 0) setShowDropdown(true); }}
          />
          {value && (
            <Button
              variant="link"
              size="sm"
              className="position-absolute top-50 end-0 translate-middle-y text-muted"
              style={{ zIndex: 5 }}
              onClick={handleClear}
              title="Vymazat"
            >
              <i className="bi bi-x-lg"></i>
            </Button>
          )}
        </div>
        {showDropdown && (
          <div className="invoice-autocomplete-dropdown">
            {loading && <div className="invoice-autocomplete-loading">Hledám…</div>}
            {!loading && results.length === 0 && search.length >= 1 && (
              <div className="invoice-autocomplete-no-results">Žádné výsledky</div>
            )}
            {!loading && results.map(inv => (
              <div key={inv.id} className="invoice-autocomplete-item" onClick={() => handleSelect(inv)}>
                <div className="d-flex justify-content-between align-items-center">
                  <span className="fw-bold">{inv.number}</span>
                  <span className="text-muted" style={{ fontSize: '.8rem' }}>{inv.type_label}</span>
                </div>
                <div className="text-muted" style={{ fontSize: '.85rem' }}>{inv.customer_name}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Form.Group>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Main component
// ════════════════════════════════════════════════════════════════════════
function InvoiceDetail({ invoiceId, openTab, can }) {
  // ── State ──────────────────────────────────────────────────────────
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  // Akce v hlavičce — export do Money, potvrzení odeslání e-mailem a mazání
  const [exportingMoney, setExportingMoney] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Zálohový cyklus — jen u dokladu typu Proforma
  const [showAdvanceModal, setShowAdvanceModal] = useState(false);
  // Dobropis se vystavuje k daňovému dokladu — i částečný (sleva za špatný inzerát)
  const [showCreditNoteModal, setShowCreditNoteModal] = useState(false);
  const [advanceInfo, setAdvanceInfo] = useState(null);
  const [creatingFinal, setCreatingFinal] = useState(false);
  const canDelete = typeof can === 'function' ? can('invoice.delete') : true;

  // Enums
  const [enums, setEnums] = useState({
    types: [],
    paymentTypes: [],
    paymentDirections: [],
    communicationMethods: [],
  });

  // Edit form state for the overview tab
  const [form, setForm] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [contactPersonLabel, setContactPersonLabel] = useState('');
  const [supplierLabel, setSupplierLabel] = useState('');

  // Item modal
  const [showItemModal, setShowItemModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [itemForm, setItemForm] = useState({});
  const [savingItem, setSavingItem] = useState(false);

  // Payment modal
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [paymentForm, setPaymentForm] = useState({});
  const [savingPayment, setSavingPayment] = useState(false);

  // ── Fetch ─────────────────────────────────────────────────────────
  const fetchInvoice = useCallback(async () => {
    setLoading(true);
    try {
      const [result, enumsResult] = await Promise.all([
        api.getInvoice(invoiceId),
        api.getInvoiceEnums(),
      ]);

      if (result.data) {
        setInvoice(result.data);
        initForm(result.data);
      }
      if (enumsResult.data) {
        setEnums(enumsResult.data);
      }
    } catch (err) {
      console.error('Failed to fetch invoice:', err);
      setError('Nepodařilo se načíst fakturu.');
    } finally {
      setLoading(false);
    }
  }, [invoiceId]);

  useEffect(() => {
    if (invoiceId) fetchInvoice();
  }, [invoiceId, fetchInvoice]);

  const initForm = (inv) => {
    setForm({
      number: inv.number || '',
      customer_order_number: inv.customer_order_number || '',
      type: inv.type || '',
      customer_name: inv.customer_name || '',
      customer_id: inv.customer_id || '',
      contact_person_id: inv.contact_person_id || '',
      creation_date: toDateTimeInput(inv.creation_date),
      due_date: toDateInput(inv.due_date),
      tax_support_date: toDateInput(inv.tax_support_date),
      sent_date: toDateInput(inv.sent_date),
      last_payment_date: toDateInput(inv.last_payment_date),
      paid: inv.paid ? true : false,
      abroad: inv.abroad ? true : false,
      reciprocal: inv.reciprocal ? true : false,
      currency: inv.currency || 'CZK',
      payment_type: inv.payment_type || '',
      communication_method: inv.communication_method || '',
      supplier_id: inv.supplier_id || '',
      responsible_person_id: inv.responsible_person_id || '',
      related_invoice_id: inv.related_invoice_id || '',
      exchange_rate_item: inv.exchange_rate_item || '',
      note: inv.note || '',
      internal_note: inv.internal_note || '',
      billing_address_id: inv.billing_address_id || '',
      shipping_address_id: inv.shipping_address_id || '',
    });
    // Popisky pro našeptávače — id samo o sobě by se zobrazilo jako prázdné pole
    setContactPersonLabel(inv.contact_person_name || '');
    setSupplierLabel(inv.supplier_name || '');
    setHasChanges(false);
  };

  const updateForm = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  // Které pole se právě změnilo samo — na dvě vteřiny zezelená, ať si toho
  // člověk všimne.
  const [zvyrazneno, setZvyrazneno] = useState(null);
  const zvyrazniTimer = useRef(null);
  const zvyrazni = (pole) => {
    setZvyrazneno(pole);
    clearTimeout(zvyrazniTimer.current);
    zvyrazniTimer.current = setTimeout(() => setZvyrazneno(null), 2000);
  };
  useEffect(() => () => clearTimeout(zvyrazniTimer.current), []);

  /**
   * Barter je na faktuře ve dvou polích: „Typ platby" se tiskne na doklad,
   * checkbox „Barter" (`invoice.reciprocal`) rozhoduje o větě o zápočtu
   * a o tom, že se vynechá QR platba. Držíme je spolu — jinak vyjde doklad
   * s formou úhrady Bárter, ale bez věty „Nehradit - zápočet!".
   */
  const zmenTypPlatby = (hodnota) => {
    const barter = BARTEROVE_UHRADY.includes(hodnota);
    setForm(prev => ({ ...prev, payment_type: hodnota, reciprocal: barter }));
    setHasChanges(true);
    if (barter !== !!form.reciprocal) zvyrazni('reciprocal');
  };

  const zmenBarter = async (zaskrtnuto) => {
    updateForm('reciprocal', zaskrtnuto);
    // Odškrtnutí typ platby nechává být — to je vědomý krok.
    if (!zaskrtnuto || BARTEROVE_UHRADY.includes(form.payment_type)) return;
    const ok = await confirm({
      title: 'Nastavit i typ platby?',
      message: 'Na doklad se bude tisknout věta o zápočtu. Má se na „Bárter" přepnout i typ platby, který je na faktuře vidět?',
      confirmText: 'Ano, nastavit Bárter',
      cancelText: 'Ne, nechat',
      icon: 'bi-arrow-left-right',
    });
    if (ok) {
      updateForm('payment_type', 'Reciprocal');
      zvyrazni('payment_type');
    }
  };

  // Kontaktní osoba se hledá napříč celou contact_person (34k+ řádků),
  // dodavatel napříč company — obojí server-side, ne předběžně načtený seznam.
  const searchContactPersons = useCallback(async (query) => {
    const res = await api.searchContactPersons(query);
    return (res.data || []).map(p => ({
      id: p.id,
      label: [
        [p.last_name, p.first_name].filter(Boolean).join(' ').trim() || `#${p.id}`,
        p.company_name,
      ].filter(Boolean).join(' — '),
    }));
  }, []);

  const searchCompanies = useCallback(async (query) => {
    const res = await api.searchCompanies(query);
    return (res.data || []).map(c => ({
      id: c.id,
      label: c.company_name || `#${c.id}`,
    }));
  }, []);

  // Stav zálohy (nepotvrzené platby, doplacení, navazující doklady) — řídí
  // tlačítka „Vytvořit potvrzení platby" a „Vytvořit vyúčtovací fakturu".
  const loadAdvanceInfo = useCallback(async () => {
    if (!invoice || invoice.type !== 'Proforma') {
      setAdvanceInfo(null);
      return;
    }
    try {
      const res = await api.getInvoiceAdvanceInfo(invoiceId);
      setAdvanceInfo(res.data || null);
    } catch (e) {
      setAdvanceInfo(null);
    }
  }, [invoice, invoiceId]);

  useEffect(() => { loadAdvanceInfo(); }, [loadAdvanceInfo]);

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(null), 3000);
  };

  // ── Save invoice ──────────────────────────────────────────────────
  const handleSave = async () => {
    // Změna kurzu přepočítá částky všech položek (jinak by doklad tiskl jeden kurz
    // a počítal druhým). Je to vidět až po uložení, tak se na to radši zeptáme.
    const staryKurz = parseFloat(String(invoice?.exchange_rate_item ?? '').replace(',', '.'));
    const novyKurz = parseFloat(String(form.exchange_rate_item ?? '').replace(',', '.'));
    const kurzSeMeni = (form.currency || 'CZK') !== 'CZK'
      && staryKurz > 0 && novyKurz > 0
      && Math.abs(staryKurz - novyKurz) > 0.000001;

    if (kurzSeMeni) {
      const ok = await confirm({
        title: 'Přepočítat doklad novým kurzem?',
        message: `Kurz se mění z ${staryKurz} na ${novyKurz} CZK za 1 ${form.currency}. `
          + 'Částky všech položek se tím přepočítají a změní se i celková cena dokladu.',
        confirmText: 'Přepočítat',
        cancelText: 'Zrušit',
        variant: 'warning',
        icon: 'bi-currency-exchange',
      });
      if (!ok) return;
    }

    setSaving(true);
    setError(null);
    try {
      const saveData = { ...form };
      // Convert booleans to ints
      saveData.paid = saveData.paid ? 1 : 0;
      saveData.abroad = saveData.abroad ? 1 : 0;
      saveData.reciprocal = saveData.reciprocal ? 1 : 0;
      // Fix creation_date format
      if (saveData.creation_date) {
        saveData.creation_date = saveData.creation_date.replace('T', ' ') + ':00';
      }

      await api.updateInvoice(invoiceId, saveData);
      showSuccess(kurzSeMeni ? 'Faktura uložena a přepočítána novým kurzem.' : 'Faktura uložena.');
      setHasChanges(false);
      fetchInvoice();
      // Otevřená záložka Fakturace se načte znovu
      window.dispatchEvent(new CustomEvent('invoices:refresh'));
    } catch (err) {
      setError('Nepodařilo se uložit: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (invoice) initForm(invoice);
  };

  // ── Item CRUD ─────────────────────────────────────────────────────
  const openItemModal = (item = null) => {
    setEditingItem(item);
    setItemForm(item ? {
      number: item.number || '',
      name: item.name || '',
      price: item.price ?? '',
      currency: item.currency || invoice?.currency || 'CZK',
      quantity: item.quantity ?? 1,
      vat_rate: item.vat_rate ?? 21,
      booking_item_id: item.booking_item_id || '',
    } : {
      number: '',
      name: '',
      price: '',
      currency: invoice?.currency || 'CZK',
      quantity: 1,
      vat_rate: 21,
      booking_item_id: '',
    });
    setShowItemModal(true);
  };

  const handleSaveItem = async () => {
    setSavingItem(true);
    try {
      const data = {
        ...itemForm,
        price: parseFloat(itemForm.price) || 0,
        quantity: parseFloat(itemForm.quantity) || 1,
        vat_rate: parseInt(itemForm.vat_rate, 10) || 0,
        booking_item_id: itemForm.booking_item_id ? parseInt(itemForm.booking_item_id) : null,
      };

      if (editingItem) {
        await api.updateInvoiceItem(editingItem.id, data);
      } else {
        await api.createInvoiceItem(invoiceId, data);
      }
      showSuccess(editingItem ? 'Položka aktualizována.' : 'Položka vytvořena.');
      setShowItemModal(false);
      fetchInvoice();
    } catch (err) {
      setError('Chyba: ' + err.message);
    } finally {
      setSavingItem(false);
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Smazat tuto položku faktury?')) return;
    try {
      await api.deleteInvoiceItem(itemId);
      showSuccess('Položka smazána.');
      fetchInvoice();
    } catch (err) {
      setError('Chyba: ' + err.message);
    }
  };

  // ── Payment CRUD ──────────────────────────────────────────────────
  const openPaymentModal = (payment = null) => {
    setEditingPayment(payment);
    setPaymentForm(payment ? {
      date: toDateInput(payment.date),
      variable_symbol: payment.variable_symbol || '',
      amount: payment.amount ?? '',
      currency: payment.currency || invoice?.currency || 'CZK',
      payment_type: payment.payment_type || '',
      payment_direction: payment.payment_direction || 'Income',
      paired: payment.paired ? true : false,
      note: payment.note || '',
      account_number: payment.account_number || '',
      code: payment.code || '',
      vat_bill: payment.vat_bill || '',
      cash_register: payment.cash_register || '',
    } : {
      date: new Date().toISOString().substring(0, 10),
      variable_symbol: invoice?.number || '',
      amount: '',
      currency: invoice?.currency || 'CZK',
      payment_type: invoice?.payment_type || '',
      payment_direction: 'Income',
      paired: false,
      note: '',
      account_number: '',
      code: '',
      vat_bill: '',
      cash_register: '',
    });
    setShowPaymentModal(true);
  };

  const handleSavePayment = async () => {
    setSavingPayment(true);
    try {
      const data = {
        ...paymentForm,
        amount: parseFloat(paymentForm.amount) || 0,
        paired: paymentForm.paired ? 1 : 0,
        variable_symbol: paymentForm.variable_symbol ? parseInt(paymentForm.variable_symbol) : null,
      };

      if (editingPayment) {
        await api.updateInvoicePayment(editingPayment.id, data);
      } else {
        await api.createInvoicePayment(invoiceId, data);
      }
      showSuccess(editingPayment ? 'Platba aktualizována.' : 'Platba vytvořena.');
      setShowPaymentModal(false);
      fetchInvoice();
      // U zálohy se tím nabídne další krok (potvrzení platby / vyúčtovací faktura).
      loadAdvanceInfo();
      window.dispatchEvent(new CustomEvent('invoices:refresh'));
    } catch (err) {
      setError('Chyba: ' + err.message);
    } finally {
      setSavingPayment(false);
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Smazat tuto platbu?')) return;
    try {
      await api.deleteInvoicePayment(paymentId);
      showSuccess('Platba smazána.');
      fetchInvoice();
    } catch (err) {
      setError('Chyba: ' + err.message);
    }
  };

  // ── Loading / Error ───────────────────────────────────────────────
  if (loading) {
    return (
      <Container fluid className="px-4">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Načítání...</span>
          </div>
        </div>
      </Container>
    );
  }

  if (!invoice) {
    return (
      <Container fluid className="px-4">
        <div className="alert alert-warning mt-4">Faktura nenalezena.</div>
      </Container>
    );
  }

  const items = invoice.items || [];
  const payments = invoice.payments || [];

  // Calculate item totals for footer
  const itemsTotal = items.reduce((sum, it) => sum + (parseFloat(it.price) || 0) * (parseFloat(it.quantity) || 0), 0);
  const paymentsTotal = payments
    .filter(p => p.payment_direction === 'Income')
    .reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);

  // ════════════════════════════════════════════════════════════════════
  // AKCE V HLAVIČCE (stejné jako ve výpisu faktur)
  // ════════════════════════════════════════════════════════════════════
  const handleExportMoney = async () => {
    if (exportingMoney) return;
    setExportingMoney(true);
    try {
      await exportMoneyXml([invoiceId]);
    } catch (e) {
      showToast(e.message, 'danger');
    } finally {
      setExportingMoney(false);
    }
  };

  const handleCreateFinalInvoice = async () => {
    if (creatingFinal) return;
    setCreatingFinal(true);
    try {
      const res = await api.createFinalInvoice(invoiceId);
      const data = res.data || {};
      showToast(`Vyúčtovací faktura ${data.number} vystavena.`, 'success');
      window.dispatchEvent(new CustomEvent('invoices:refresh'));
      loadAdvanceInfo();
      if (data.id && openTab) {
        openTab(`invoice-${data.id}`, `Faktura ${data.number}`, `/invoices/${data.id}`, 'InvoiceDetail', { invoiceId: data.id });
      }
    } catch (e) {
      showToast(e.message || 'Vyúčtovací fakturu se nepodařilo vystavit.', 'danger');
    } finally {
      setCreatingFinal(false);
    }
  };

  const handleRestore = async () => {
    try {
      await api.restoreInvoice(invoiceId);
      showToast('Faktura byla obnovená.', 'success');
      window.dispatchEvent(new CustomEvent('invoices:refresh'));
      window.dispatchEvent(new CustomEvent('orders:refresh'));
      fetchInvoice();
    } catch (e) {
      showToast(e.message || 'Fakturu se nepodařilo obnovit.', 'danger');
    }
  };

  // ════════════════════════════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════════════════════════════
  return (
    <Container fluid className="px-4">
      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
      {successMsg && <Alert variant="success" dismissible onClose={() => setSuccessMsg(null)}>{successMsg}</Alert>}

      {/* ===== HEADER ===== */}
      <div className="invoice-detail-header">
        <div className="d-flex justify-content-between align-items-start flex-wrap">
          <div>
            <div className="d-flex align-items-center gap-2 mb-1">
              <span className="invoice-number">
                <i className="bi bi-receipt me-2"></i>
                {nazevDokladu(invoice.type, invoice.type_label)} {invoice.number || invoice.id}
              </span>
              <StatusBadge status={invoice.status} />
              {invoice.deleted_at ? (
                <Badge bg="danger" title={invoice.deleted_by_name ? `Smazal/a ${invoice.deleted_by_name}` : undefined}>
                  <i className="bi bi-trash me-1"></i>Smazáno {formatDate(invoice.deleted_at)}
                </Badge>
              ) : null}
              {invoice.abroad ? <Badge bg="info">Zahraniční</Badge> : null}
              {invoice.reciprocal ? <Badge bg="warning" text="dark">Barter</Badge> : null}
            </div>
            <div className="invoice-meta">
              {invoice.type_label || invoice.type} &middot; {invoice.customer_name}
              {invoice.currency && invoice.currency !== 'CZK' ? ` · ${invoice.currency}` : ''}
            </div>
          </div>
          <div className="d-flex gap-2 align-items-center">
            {invoice.type === 'Invoice' && !invoice.deleted_at && (
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip>Opravný daňový doklad — i jen na část částky (sleva za reklamaci)</Tooltip>}
              >
                <Button variant="outline-primary" size="sm" onClick={() => setShowCreditNoteModal(true)}>
                  <i className="bi bi-arrow-counterclockwise me-1"></i>Dobropis
                </Button>
              </OverlayTrigger>
            )}
            {invoice.type === 'Proforma' && !invoice.deleted_at && (
              <>
                <OverlayTrigger
                  placement="top"
                  overlay={(
                    <Tooltip>
                      {advanceInfo && advanceInfo.pending_payments.length === 0
                        ? 'Potvrzení se vystavuje k přijaté platbě — zatím žádná nepotvrzená není'
                        : 'Daňový doklad k přijaté platbě (řada 2613)'}
                    </Tooltip>
                  )}
                >
                  <span>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      disabled={!advanceInfo || advanceInfo.pending_payments.length === 0}
                      onClick={() => setShowAdvanceModal(true)}
                    >
                      <i className="bi bi-receipt-cutoff me-1"></i>Potvrzení platby
                    </Button>
                  </span>
                </OverlayTrigger>
                <OverlayTrigger
                  placement="top"
                  overlay={(
                    <Tooltip>
                      {advanceInfo?.has_final_invoice
                        ? 'K této záloze už vyúčtovací faktura existuje'
                        : advanceInfo && !advanceInfo.fully_paid
                          ? 'Vyúčtovací faktura se vystavuje až po doplacení zálohy'
                          : 'Vyúčtovací faktura k doplacené záloze'}
                    </Tooltip>
                  )}
                >
                  <span>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      disabled={!advanceInfo || !advanceInfo.fully_paid || advanceInfo.has_final_invoice || creatingFinal}
                      onClick={handleCreateFinalInvoice}
                    >
                      {creatingFinal
                        ? <><span className="spinner-border spinner-border-sm me-1" role="status"></span>Vystavuji…</>
                        : <><i className="bi bi-file-earmark-text me-1"></i>Vyúčtovací faktura</>}
                    </Button>
                  </span>
                </OverlayTrigger>
              </>
            )}
            <Button
              variant="primary"
              size="sm"
              onClick={() => window.open(api.getInvoicePdfUrl(invoiceId), '_blank', 'noopener')}
              title="Otevřít fakturu jako PDF v novém okně"
            >
              <i className="bi bi-file-earmark-pdf me-1"></i>PDF
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={exportingMoney}
              onClick={handleExportMoney}
              title="Vygeneruje XML feed pro import do Money S3"
            >
              <i className="bi bi-filetype-xml me-1"></i>{exportingMoney ? 'Generuji…' : 'Export XML'}
            </Button>
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip>{invoice.communication_method === 'POST' ? 'Doklad se posílá poštou' : 'Poslat fakturu e-mailem'}</Tooltip>}
            >
              <span>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  disabled={invoice.communication_method === 'POST' || !!invoice.deleted_at}
                  onClick={() => setShowEmailModal(true)}
                >
                  <i className="bi bi-envelope me-1"></i>Poslat e-mailem
                </Button>
              </span>
            </OverlayTrigger>
            {invoice.deleted_at ? (
              <OverlayTrigger placement="top" overlay={<Tooltip>{canDelete ? 'Vrátit fakturu mezi platné doklady' : 'Nemáš oprávnění obnovovat faktury'}</Tooltip>}>
                <span>
                  <Button variant="outline-success" size="sm" disabled={!canDelete} onClick={handleRestore}>
                    <i className="bi bi-arrow-counterclockwise me-1"></i>Obnovit
                  </Button>
                </span>
              </OverlayTrigger>
            ) : (
              <OverlayTrigger placement="top" overlay={<Tooltip>{canDelete ? 'Smazat fakturu' : 'Nemáš oprávnění mazat faktury'}</Tooltip>}>
                <span>
                  <Button variant="outline-danger" size="sm" disabled={!canDelete} onClick={() => setShowDeleteModal(true)}>
                    <i className="bi bi-trash me-1"></i>Smazat
                  </Button>
                </span>
              </OverlayTrigger>
            )}
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => openTab('invoices', 'Fakturace', '/invoices', 'InvoiceList')}
            >
              <i className="bi bi-arrow-left me-1"></i>Zpět na seznam
            </Button>
          </div>
        </div>
      </div>

      {/* ===== TABS ===== */}
      <div className="invoice-detail-tabs-wrapper">
        <Nav variant="tabs" className="invoice-detail-tabs" activeKey={activeTab} onSelect={setActiveTab}>
          <Nav.Item>
            <Nav.Link eventKey="overview">
              <i className="bi bi-info-circle me-1"></i>Přehled
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="items">
              <i className="bi bi-list-ul me-1"></i>Položky
              {items.length > 0 && <Badge bg="secondary" className="ms-1">{items.length}</Badge>}
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="payments">
              <i className="bi bi-credit-card me-1"></i>Platby
              {payments.length > 0 && <Badge bg="secondary" className="ms-1">{payments.length}</Badge>}
            </Nav.Link>
          </Nav.Item>
        </Nav>
      </div>

      {/* ===== TAB CONTENT ===== */}
      <div className="invoice-tab-content invoice-detail-form">

        {/* ── PŘEHLED ─────────────────────────────────────────────── */}
        {activeTab === 'overview' && (
          <>
            {/* Zálohový cyklus — nabídka dalšího kroku */}
            {invoice.type === 'Proforma' && !invoice.deleted_at && advanceInfo && (
              advanceInfo.pending_payments.length > 0 ? (
                <Alert variant="info" className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                  <div>
                    <i className="bi bi-info-circle me-2"></i>
                    Na záloze je přijatá platba {formatPrice(advanceInfo.pending_amount, invoice.currency)},
                    ke které zatím není potvrzení platby.
                  </div>
                  <Button variant="primary" size="sm" onClick={() => setShowAdvanceModal(true)}>
                    <i className="bi bi-receipt-cutoff me-1"></i>Vytvořit potvrzení platby
                  </Button>
                </Alert>
              ) : advanceInfo.fully_paid && !advanceInfo.has_final_invoice ? (
                <Alert variant="info" className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                  <div>
                    <i className="bi bi-info-circle me-2"></i>
                    Záloha je doplacená a platby jsou potvrzené — zbývá vystavit vyúčtovací fakturu.
                  </div>
                  <Button variant="primary" size="sm" onClick={handleCreateFinalInvoice} disabled={creatingFinal}>
                    <i className="bi bi-file-earmark-text me-1"></i>Vytvořit vyúčtovací fakturu
                  </Button>
                </Alert>
              ) : null
            )}

            {/* Související doklady — zálohový řetězec */}
            {(invoice.related_documents || []).length > 0 && (
              <div className="mb-4">
                <h6 className="mb-2"><i className="bi bi-diagram-3 me-2"></i>Související doklady</h6>
                <Table size="sm" hover className="align-middle mb-0">
                  <tbody>
                    {invoice.related_documents.map((doc) => (
                      <tr key={doc.id} role="button" onClick={() => openTab(`invoice-${doc.id}`, `Faktura ${doc.number}`, `/invoices/${doc.id}`, 'InvoiceDetail', { invoiceId: doc.id })}>
                        <td className="text-nowrap">
                          <span className="link-document">{doc.number || doc.id}</span>
                          {doc.deleted && <Badge bg="danger" className="ms-2">Smazáno</Badge>}
                        </td>
                        <td className="small text-muted">{doc.type_label}</td>
                        <td className="text-end text-nowrap">{formatPrice(doc.total_price, doc.currency)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}

            {/* Summary cards at top */}
            <Row className="g-3 mb-4">
              <Col md={4}>
                <div className="invoice-summary-card">
                  <div className="summary-row">
                    <span className="summary-label">Základ (bez DPH)</span>
                    <span className="summary-value">{formatPrice(invoice.net_net_price, invoice.currency)}</span>
                  </div>
                  <div className="summary-row">
                    <span className="summary-label">DPH</span>
                    <span className="summary-value">{formatPrice(invoice.vat_amount, invoice.currency)}</span>
                  </div>
                  <div className="summary-row">
                    <span className="summary-label">Celkem (s DPH)</span>
                    <span className="summary-value">{formatPrice(invoice.total_price, invoice.currency)}</span>
                  </div>
                  <div className="summary-row">
                    <span className="summary-label">Uhrazeno</span>
                    <span className="summary-value text-success">{formatPrice(invoice.payed_amount, invoice.currency)}</span>
                  </div>
                  <div className="summary-row total">
                    <span className="summary-label">Zbývá uhradit</span>
                    <span className={`summary-value ${parseFloat(invoice.to_be_payed) > 0 ? 'text-danger' : 'text-success'}`}>
                      {formatPrice(invoice.to_be_payed, invoice.currency)}
                    </span>
                  </div>
                </div>
              </Col>
            </Row>

            {/* Editable form */}
            <div className="invoice-form-section">
              <h6><i className="bi bi-file-text"></i>Základní údaje</h6>
              <Row className="g-3">
                <Col md={2}>
                  <Form.Group>
                    <Form.Label>Číslo dokladu</Form.Label>
                    <Form.Control value={form.number || ''} onChange={e => updateForm('number', e.target.value)} />
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group>
                    <Form.Label>Typ faktury</Form.Label>
                    <Form.Select value={form.type || ''} onChange={e => updateForm('type', e.target.value)}>
                      <option value="">—</option>
                      {enums.types.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Zákazník</Form.Label>
                    <Form.Control value={form.customer_name || ''} onChange={e => updateForm('customer_name', e.target.value)} />
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group>
                    <Form.Label>Měna</Form.Label>
                    <Form.Control value={form.currency || ''} onChange={e => updateForm('currency', e.target.value)} />
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group>
                    <Form.Label>Kurz</Form.Label>
                    <Form.Control value={form.exchange_rate_item || ''} onChange={e => updateForm('exchange_rate_item', e.target.value)} />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Číslo objednávky zákazníka</Form.Label>
                    <Form.Control
                      value={form.customer_order_number || ''}
                      onChange={e => updateForm('customer_order_number', e.target.value)}
                      maxLength={255}
                      placeholder="např. 26obj03080"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>

            <div className="invoice-form-section">
              <h6><i className="bi bi-calendar3"></i>Data</h6>
              <Row className="g-3">
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Datum vystavení</Form.Label>
                    <Form.Control type="datetime-local" value={form.creation_date || ''} onChange={e => updateForm('creation_date', e.target.value)} />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>DÚZP</Form.Label>
                    <Form.Control type="date" value={form.tax_support_date || ''} onChange={e => updateForm('tax_support_date', e.target.value)} />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Datum splatnosti</Form.Label>
                    <Form.Control type="date" value={form.due_date || ''} onChange={e => updateForm('due_date', e.target.value)} />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Datum odeslání</Form.Label>
                    <Form.Control type="date" value={form.sent_date || ''} onChange={e => updateForm('sent_date', e.target.value)} />
                  </Form.Group>
                </Col>
              </Row>
            </div>

            <div className="invoice-form-section">
              <h6><i className="bi bi-gear"></i>Platba a komunikace</h6>
              <Row className="g-3">
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Typ platby</Form.Label>
                    <Form.Select
                      className={zvyrazneno === 'payment_type' ? 'pole-zvyrazneno' : ''}
                      value={form.payment_type || ''}
                      onChange={e => zmenTypPlatby(e.target.value)}
                    >
                      <option value="">—</option>
                      {enums.paymentTypes.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Komunikace</Form.Label>
                    <Form.Select value={form.communication_method || ''} onChange={e => updateForm('communication_method', e.target.value)}>
                      <option value="">—</option>
                      {enums.communicationMethods.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <RelatedInvoiceSearch
                    value={form.related_invoice_id}
                    displayName={invoice.related_invoice_number_display || ''}
                    onChange={(inv) => {
                      if (inv) {
                        updateForm('related_invoice_id', inv.id);
                      } else {
                        updateForm('related_invoice_id', '');
                      }
                    }}
                  />
                </Col>
                <Col md={3} className="d-flex align-items-end gap-3">
                  <Form.Check id="faktura-uhrazeno" type="checkbox" label="Uhrazeno" checked={form.paid || false} onChange={e => updateForm('paid', e.target.checked)} />
                  <Form.Check id="faktura-zahranicni" type="checkbox" label="Zahraniční" checked={form.abroad || false} onChange={e => updateForm('abroad', e.target.checked)} />
                  <Form.Check
                    className={zvyrazneno === 'reciprocal' ? 'pole-zvyrazneno' : ''}
                    id="faktura-barter"
                    type="checkbox"
                    label="Barter"
                    checked={form.reciprocal || false}
                    onChange={e => zmenBarter(e.target.checked)}
                  />
                </Col>
              </Row>
            </div>

            <div className="invoice-form-section">
              <h6><i className="bi bi-chat-left-text"></i>Poznámky</h6>
              <Row className="g-3">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Poznámka</Form.Label>
                    <Form.Control as="textarea" rows={3} value={form.note || ''} onChange={e => updateForm('note', e.target.value)} />
                    <Form.Text className="text-muted">Tiskne se na faktuře nad položkami.</Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            </div>

            <div className="invoice-form-section">
              <h6><i className="bi bi-geo-alt"></i>Adresy</h6>
              <Row className="g-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Fakturační adresa</Form.Label>
                    <Form.Select value={form.billing_address_id || ''} onChange={e => updateForm('billing_address_id', e.target.value ? parseInt(e.target.value) : '')}>
                      <option value="">— Bez adresy —</option>
                      {(invoice.customer_addresses || []).map(a => (
                        <option key={a.id} value={a.id}>{a.display_label}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Doručovací adresa</Form.Label>
                    <Form.Select value={form.shipping_address_id || ''} onChange={e => updateForm('shipping_address_id', e.target.value ? parseInt(e.target.value) : '')}>
                      <option value="">— Bez adresy —</option>
                      {(invoice.customer_addresses || []).map(a => (
                        <option key={a.id} value={a.id}>{a.display_label}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            </div>

            <div className="invoice-form-section">
              <h6><i className="bi bi-people"></i>Osoby a dodavatel</h6>
              <Row className="g-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Kontaktní osoba</Form.Label>
                    <SearchableSelect
                      value={form.contact_person_id || ''}
                      selectedLabel={contactPersonLabel}
                      onSearch={searchContactPersons}
                      onChange={(id, opt) => {
                        updateForm('contact_person_id', id ? parseInt(id) : '');
                        setContactPersonLabel(opt ? opt.label : '');
                      }}
                      placeholder="Hledat podle jména nebo e-mailu…"
                      emptyLabel="— Bez kontaktu —"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Dodavatel</Form.Label>
                    <SearchableSelect
                      value={form.supplier_id || ''}
                      selectedLabel={supplierLabel}
                      onSearch={searchCompanies}
                      onChange={(id, opt) => {
                        updateForm('supplier_id', id ? parseInt(id) : '');
                        setSupplierLabel(opt ? opt.label : '');
                      }}
                      placeholder="Hledat firmu…"
                      emptyLabel="— Bez dodavatele —"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>

            {/* Smazaný doklad se needituje — nejdřív se musí obnovit */}
            {invoice.deleted_at && (
              <Alert variant="danger" className="mt-3 mb-0">
                <i className="bi bi-trash me-2"></i>
                Doklad je smazaný{invoice.deleted_by_name ? ` (smazal/a ${invoice.deleted_by_name})` : ''}
                {invoice.delete_reason ? ` — ${invoice.delete_reason}` : ''}. Změny se neuloží, dokud ho neobnovíš.
              </Alert>
            )}

            {/* Save bar */}
            {hasChanges && !invoice.deleted_at && (
              <div className="invoice-save-bar">
                <Button variant="outline-secondary" onClick={handleCancel} disabled={saving}>
                  Zrušit změny
                </Button>
                <Button variant="primary" onClick={handleSave} disabled={saving}>
                  {saving ? (
                    <><span className="spinner-border spinner-border-sm me-1" role="status"></span>Ukládám...</>
                  ) : (
                    <><i className="bi bi-check-lg me-1"></i>Uložit</>
                  )}
                </Button>
              </div>
            )}
          </>
        )}

        {/* ── PLATBY ─────────────────────────────────────────────── */}
        {activeTab === 'payments' && (
          <>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="mb-0">
                <i className="bi bi-credit-card me-2"></i>
                Platby ({payments.length})
              </h6>
              <Button size="sm" variant="primary" onClick={() => openPaymentModal()}>
                <i className="bi bi-plus-lg me-1"></i>Přidat platbu
              </Button>
            </div>

            {payments.length === 0 ? (
              <div className="invoice-empty-state">
                <i className="bi bi-credit-card"></i>
                <div>Zatím žádné platby</div>
              </div>
            ) : (
              <div className="table-responsive">
                <Table hover className="align-middle invoice-items-table">
                  <thead className="table-light">
                    <tr>
                      <th>Datum</th>
                      <th>VS</th>
                      <th>Směr</th>
                      <th>Typ platby</th>
                      <th className="text-end">Částka</th>
                      <th>Měna</th>
                      <th>Spárováno</th>
                      <th>Číslo účtu</th>
                      <th>Poznámka</th>
                      <th className="text-end">Akce</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map(p => (
                      <tr key={p.id}>
                        <td>{formatDate(p.date)}</td>
                        <td><span className="vs-list">{p.variable_symbol || '—'}</span></td>
                        <td><DirectionBadge direction={p.payment_direction} /></td>
                        <td>
                          {p.payment_type
                            ? <span className="badge-payment-type">{p.payment_type}</span>
                            : <span className="text-muted">—</span>}
                        </td>
                        <td className="item-price">{formatPrice(p.amount)}</td>
                        <td>{p.currency || '—'}</td>
                        <td>
                          {p.paired
                            ? <span className="badge-paired">Spárováno</span>
                            : <span className="badge-unpaired">Nespárováno</span>}
                        </td>
                        <td><span className="text-muted" style={{ fontSize: '.85rem' }}>{p.account_number || '—'}</span></td>
                        <td>
                          <span title={p.note || ''} style={{ maxWidth: '150px', display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '.85rem' }}>
                            {p.note || '—'}
                          </span>
                        </td>
                        <td>
                          <div className="item-actions">
                            <OverlayTrigger placement="top" overlay={<Tooltip>Upravit</Tooltip>}>
                              <Button size="sm" variant="outline-primary" onClick={() => openPaymentModal(p)}>
                                <i className="bi bi-pencil"></i>
                              </Button>
                            </OverlayTrigger>
                            <OverlayTrigger placement="top" overlay={<Tooltip>Smazat</Tooltip>}>
                              <Button size="sm" variant="outline-danger" onClick={() => handleDeletePayment(p.id)}>
                                <i className="bi bi-trash"></i>
                              </Button>
                            </OverlayTrigger>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </>
        )}

        {/* ── POLOŽKY ────────────────────────────────────────────── */}
        {activeTab === 'items' && (
          <>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="mb-0">
                <i className="bi bi-list-ul me-2"></i>
                Položky faktury ({items.length})
              </h6>
              <Button size="sm" variant="primary" onClick={() => openItemModal()}>
                <i className="bi bi-plus-lg me-1"></i>Přidat položku
              </Button>
            </div>

            {items.length === 0 ? (
              <div className="invoice-empty-state">
                <i className="bi bi-list-ul"></i>
                <div>Zatím žádné položky</div>
              </div>
            ) : (
              <div className="table-responsive">
                <Table hover className="align-middle invoice-items-table">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: '90px' }}>Číslo</th>
                      <th>Název</th>
                      <th>Obj. položka</th>
                      <th className="text-end" style={{ width: '120px' }}>Cena/ks (bez DPH)</th>
                      <th className="text-end" style={{ width: '80px' }}>Množství</th>
                      <th className="text-end" style={{ width: '90px' }}>Sazba DPH</th>
                      <th>Měna</th>
                      <th className="text-end" style={{ width: '140px' }}>Celkem (bez DPH)</th>
                      <th className="text-end" style={{ width: '100px' }}>Akce</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map(it => {
                      const lineTotal = (parseFloat(it.price) || 0) * (parseFloat(it.quantity) || 0);
                      return (
                        <tr key={it.id}>
                          <td><span className="item-number">{it.number || '—'}</span></td>
                          <td>
                            <span className="item-name" title={it.name}>
                              {it.name || '—'}
                            </span>
                          </td>
                          <td>
                            <span className="text-muted" style={{ fontSize: '.85rem' }}>
                              {it.booking_item_number || (it.booking_item_id ? `#${it.booking_item_id}` : '—')}
                            </span>
                          </td>
                          <td className="item-price">{formatPrice(it.price)}</td>
                          <td className="text-end">{it.quantity ?? '—'}</td>
                          <td className="text-end">{(it.vat_rate ?? 0)} %</td>
                          <td>{it.currency || '—'}</td>
                          <td className="item-total">{formatPrice(lineTotal)}</td>
                          <td>
                            <div className="item-actions">
                              <OverlayTrigger placement="top" overlay={<Tooltip>Upravit</Tooltip>}>
                                <Button size="sm" variant="outline-primary" onClick={() => openItemModal(it)}>
                                  <i className="bi bi-pencil"></i>
                                </Button>
                              </OverlayTrigger>
                              <OverlayTrigger placement="top" overlay={<Tooltip>Smazat</Tooltip>}>
                                <Button size="sm" variant="outline-danger" onClick={() => handleDeleteItem(it.id)}>
                                  <i className="bi bi-trash"></i>
                                </Button>
                              </OverlayTrigger>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {/* Totals row */}
                    <tr className="items-total-row">
                      <td colSpan={7} className="text-end"><strong>Celkem (bez DPH)</strong></td>
                      <td className="item-total">{formatPrice(itemsTotal, invoice.currency)}</td>
                      <td></td>
                    </tr>
                  </tbody>
                </Table>
              </div>
            )}
          </>
        )}
      </div>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* MODALS */}
      {/* ═══════════════════════════════════════════════════════════════ */}

      {/* ── Item modal ─────────────────────────────────────────────── */}
      <Modal show={showItemModal} onHide={() => setShowItemModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-list-ul me-2"></i>
            {editingItem ? 'Upravit položku' : 'Nová položka'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col md={3}>
              <Form.Group>
                <Form.Label>Číslo položky</Form.Label>
                <Form.Control
                  value={itemForm.number || ''}
                  onChange={e => setItemForm(p => ({ ...p, number: e.target.value }))}
                  placeholder="ITM001"
                />
              </Form.Group>
            </Col>
            <Col md={9}>
              <Form.Group>
                <Form.Label>Název</Form.Label>
                <Form.Control
                  value={itemForm.name || ''}
                  onChange={e => setItemForm(p => ({ ...p, name: e.target.value }))}
                  placeholder="Popis položky"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Cena za kus</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  value={itemForm.price ?? ''}
                  onChange={e => setItemForm(p => ({ ...p, price: e.target.value }))}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Množství</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  value={itemForm.quantity ?? ''}
                  onChange={e => setItemForm(p => ({ ...p, quantity: e.target.value }))}
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>Sazba DPH</Form.Label>
                <Form.Select
                  value={itemForm.vat_rate ?? 21}
                  onChange={e => setItemForm(p => ({ ...p, vat_rate: e.target.value }))}
                >
                  <option value={21}>21 %</option>
                  <option value={15}>15 %</option>
                  <option value={12}>12 %</option>
                  <option value={10}>10 %</option>
                  <option value={0}>0 %</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>Měna</Form.Label>
                <Form.Control
                  value={itemForm.currency || ''}
                  onChange={e => setItemForm(p => ({ ...p, currency: e.target.value }))}
                />
              </Form.Group>
            </Col>
            {/* Vazba na položku objednávky (booking_item_id) se needituje ručně —
                drží se ve stavu formuláře, aby se při úpravě položky neztratila. */}
            {/* Preview total */}
            <Col md={12}>
              {(() => {
                const lineNet = (parseFloat(itemForm.price) || 0) * (parseFloat(itemForm.quantity) || 0);
                const rate = parseFloat(itemForm.vat_rate) || 0;
                const lineVat = lineNet * rate / 100;
                return (
                  <div className="invoice-summary-card mt-2">
                    <div className="summary-row">
                      <span className="summary-label">Základ (bez DPH)</span>
                      <span className="summary-value">{formatPrice(lineNet, itemForm.currency)}</span>
                    </div>
                    <div className="summary-row">
                      <span className="summary-label">DPH ({rate} %)</span>
                      <span className="summary-value">{formatPrice(lineVat, itemForm.currency)}</span>
                    </div>
                    <div className="summary-row total">
                      <span className="summary-label">Celkem za položku (s DPH)</span>
                      <span className="summary-value">{formatPrice(lineNet + lineVat, itemForm.currency)}</span>
                    </div>
                  </div>
                );
              })()}
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowItemModal(false)}>Zrušit</Button>
          <Button variant="primary" onClick={handleSaveItem} disabled={savingItem}>
            {savingItem ? 'Ukládám...' : 'Uložit'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Payment modal ──────────────────────────────────────────── */}
      <Modal show={showPaymentModal} onHide={() => setShowPaymentModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-credit-card me-2"></i>
            {editingPayment ? 'Upravit platbu' : 'Nová platba'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Datum platby</Form.Label>
                <Form.Control
                  type="date"
                  value={paymentForm.date || ''}
                  onChange={e => setPaymentForm(p => ({ ...p, date: e.target.value }))}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Variabilní symbol</Form.Label>
                <Form.Control
                  value={paymentForm.variable_symbol || ''}
                  onChange={e => setPaymentForm(p => ({ ...p, variable_symbol: e.target.value }))}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Směr</Form.Label>
                <Form.Select
                  value={paymentForm.payment_direction || ''}
                  onChange={e => setPaymentForm(p => ({ ...p, payment_direction: e.target.value }))}
                >
                  <option value="">—</option>
                  {enums.paymentDirections.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Částka</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  value={paymentForm.amount ?? ''}
                  onChange={e => setPaymentForm(p => ({ ...p, amount: e.target.value }))}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Měna</Form.Label>
                <Form.Control
                  value={paymentForm.currency || ''}
                  onChange={e => setPaymentForm(p => ({ ...p, currency: e.target.value }))}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Typ platby</Form.Label>
                <Form.Select
                  value={paymentForm.payment_type || ''}
                  onChange={e => setPaymentForm(p => ({ ...p, payment_type: e.target.value }))}
                >
                  <option value="">—</option>
                  {enums.paymentTypes.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={2} className="d-flex align-items-end">
              <Form.Check
                type="checkbox"
                label="Spárováno"
                checked={paymentForm.paired || false}
                onChange={e => setPaymentForm(p => ({ ...p, paired: e.target.checked }))}
              />
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Číslo účtu</Form.Label>
                <Form.Control
                  value={paymentForm.account_number || ''}
                  onChange={e => setPaymentForm(p => ({ ...p, account_number: e.target.value }))}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Kód</Form.Label>
                <Form.Control
                  value={paymentForm.code || ''}
                  onChange={e => setPaymentForm(p => ({ ...p, code: e.target.value }))}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Daň. doklad</Form.Label>
                <Form.Control
                  value={paymentForm.vat_bill || ''}
                  onChange={e => setPaymentForm(p => ({ ...p, vat_bill: e.target.value }))}
                />
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Group>
                <Form.Label>Poznámka</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={paymentForm.note || ''}
                  onChange={e => setPaymentForm(p => ({ ...p, note: e.target.value }))}
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPaymentModal(false)}>Zrušit</Button>
          <Button variant="primary" onClick={handleSavePayment} disabled={savingPayment}>
            {savingPayment ? 'Ukládám...' : 'Uložit'}
          </Button>
        </Modal.Footer>
      </Modal>

      <InvoiceCreditNoteModal
        show={showCreditNoteModal}
        invoiceId={invoiceId}
        onHide={() => { setShowCreditNoteModal(false); fetchInvoice(); }}
        openTab={openTab}
      />

      <InvoiceAdvanceModal
        show={showAdvanceModal}
        invoiceId={invoiceId}
        onHide={() => { setShowAdvanceModal(false); loadAdvanceInfo(); fetchInvoice(); }}
        openTab={openTab}
      />

      <InvoiceDeleteModal
        show={showDeleteModal}
        invoiceId={invoiceId}
        onHide={() => setShowDeleteModal(false)}
        onDeleted={() => fetchInvoice()}
      />

      <InvoiceEmailModal
        show={showEmailModal}
        invoices={showEmailModal ? [{ id: invoiceId, number: invoice.number }] : null}
        onHide={() => setShowEmailModal(false)}
        onSent={() => fetchInvoice()}
      />

    </Container>
  );
}

export default InvoiceDetail;
